import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [settings, setSettings] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    profession: '',
    company: '',
    membership_interest: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'Club JCI Ltd - Contact';
    
    const fetchData = async () => {
      try {
        const [settingsRes, tiersRes] = await Promise.all([
          axios.get(`${API}/settings`),
          axios.get(`${API}/membership-tiers`)
        ]);
        setSettings(settingsRes.data);
        setTiers(tiersRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/inquiries`, formData);
      setIsSubmitted(true);
      toast.success('Your inquiry has been submitted successfully!');
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        profession: '',
        company: '',
        membership_interest: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="contact-success" className="pt-24 min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md px-4"
        >
          <div className="w-20 h-20 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h2 className="text-3xl font-semibold text-[#F1F5F9] mb-4">Thank You!</h2>
          <p className="text-[#94A3B8] mb-8">
            Your membership inquiry has been received. Our team will contact you shortly.
          </p>
          <button
            onClick={() => setIsSubmitted(false)}
            className="btn-gold text-sm py-3 px-8 rounded-sm"
          >
            Submit Another Inquiry
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="pt-24">
      {/* Hero Section */}
      <section className="section-padding marble-overlay" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4">Contact</p>
            <h1 className="text-4xl sm:text-5xl font-semibold text-[#F1F5F9] mb-6">
              Begin Your <span className="gold-text">Journey</span>
            </h1>
            <p className="text-lg text-[#94A3B8] leading-relaxed">
              Ready to join Dhaka's most prestigious professional community? 
              Submit your membership inquiry below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-[#0A192F]" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <h2 className="text-2xl font-semibold text-[#F1F5F9] mb-8">Get In Touch</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F1F5F9] font-medium mb-1">Address</p>
                    <p className="text-[#94A3B8] text-sm">
                      {settings?.address || 'House: 11A, Road: 113, Gulshan 02, Dhaka'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F1F5F9] font-medium mb-1">Phone</p>
                    <p className="text-[#94A3B8] text-sm">
                      <a href={`tel:${settings?.phone1}`} className="hover:text-[#D4AF37] transition-colors block">
                        {settings?.phone1 || '+8801675381845'}
                      </a>
                      <a href={`tel:${settings?.phone2}`} className="hover:text-[#D4AF37] transition-colors block">
                        {settings?.phone2 || '+8801342689009'}
                      </a>
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-[#F1F5F9] font-medium mb-1">Email</p>
                    <p className="text-[#94A3B8] text-sm">
                      <a href={`mailto:${settings?.email1}`} className="hover:text-[#D4AF37] transition-colors block">
                        {settings?.email1 || 'contact@clubjci.com'}
                      </a>
                      <a href={`mailto:${settings?.email2}`} className="hover:text-[#D4AF37] transition-colors block">
                        {settings?.email2 || 'clubjcibd@gmail.com'}
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="aspect-video rounded-sm overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3650.5896!2d90.4153!3d23.7925!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDQ3JzMzLjAiTiA5MMKwMjQnNTUuMSJF!5e0!3m2!1sen!2sbd!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Club JCI Location"
                ></iframe>
              </div>
            </motion.div>

            {/* Inquiry Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <div className="glass-card p-8 md:p-10 rounded-sm">
                <h2 className="text-2xl font-semibold text-[#F1F5F9] mb-8">Membership Inquiry</h2>
                
                <form onSubmit={handleSubmit} data-testid="inquiry-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input
                        type="text"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        required
                        data-testid="input-full-name"
                        className="w-full rounded-sm"
                        placeholder="Your full name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="input-email"
                        className="w-full rounded-sm"
                        placeholder="Your email address"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="input-phone"
                        className="w-full rounded-sm"
                        placeholder="Your phone number"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Profession *</label>
                      <input
                        type="text"
                        name="profession"
                        value={formData.profession}
                        onChange={handleChange}
                        required
                        data-testid="input-profession"
                        className="w-full rounded-sm"
                        placeholder="Your profession"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Company (Optional)</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="input-company"
                        className="w-full rounded-sm"
                        placeholder="Your company name"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Membership Interest *</label>
                      <select
                        name="membership_interest"
                        value={formData.membership_interest}
                        onChange={handleChange}
                        required
                        data-testid="select-membership"
                        className="w-full rounded-sm"
                      >
                        <option value="">Select a tier</option>
                        {tiers.map(tier => (
                          <option key={tier.id} value={tier.name}>{tier.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group mt-6">
                    <label className="form-label">Message (Optional)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      data-testid="textarea-message"
                      className="w-full rounded-sm resize-none"
                      placeholder="Tell us about yourself and why you're interested in joining Club JCI..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-inquiry-btn"
                    className="btn-gold-filled text-sm py-4 px-10 rounded-sm flex items-center gap-2 mt-6 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="spinner w-4 h-4"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        Submit Inquiry
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
